import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { FC } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { atlasRoutes } from "@/app/router/routes";
import { Table, Flex, Heading, Text, Button, Card } from "@radix-ui/themes";
import { StatusBadge } from "./components/status-badge";
import { RelativeTime } from "./components/relative-time";
import { Id } from "../../../convex/_generated/dataModel";

export const MembotThreadDetailPage: FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();

  const thread = useQuery(
    api.threads.getThread,
    threadId ? { threadId: threadId as Id<"assistantThreads"> } : "skip"
  );
  const tasks = useQuery(
    api.threads.listTasks,
    threadId ? { threadId: threadId as Id<"assistantThreads"> } : "skip"
  );
  const reminders = useQuery(
    api.threads.listReminders,
    threadId ? { threadId: threadId as Id<"assistantThreads"> } : "skip"
  );
  const activities = useQuery(
    api.threads.listThreadActivities,
    threadId
      ? { threadId: threadId as Id<"assistantThreads">, limit: 100 }
      : "skip"
  );

  if (!threadId) return <Text>Missing thread ID</Text>;
  if (thread === undefined)
    return <Text color="gray">Loading thread...</Text>;
  if (thread === null) return <Text color="red">Thread not found.</Text>;

  return (
    <div>
      <Flex direction="column" gap="1" mb="4">
        <Button
          variant="ghost"
          size="1"
          onClick={() => navigate(-1)}
          style={{ alignSelf: "flex-start" }}
        >
          &larr; Back
        </Button>
        <Flex align="center" gap="3">
          <Heading size="5">{thread.title}</Heading>
          <StatusBadge status={thread.status} size="2" />
        </Flex>
        <Flex gap="2">
          <Text size="1" color="gray">
            Session: {thread.sessionId.slice(0, 12)}...
          </Text>
          <Text size="1" color="gray">
            Created: <RelativeTime timestamp={thread.createdAt} />
          </Text>
          <Text size="1" color="gray">
            Last activity: <RelativeTime timestamp={thread.lastActivityAt} />
          </Text>
        </Flex>
      </Flex>

      {/* Tasks Section */}
      <Heading size="3" mb="2" mt="4">
        Tasks
      </Heading>
      <Table.Root variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Title</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Description</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Due</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(tasks ?? []).map((task) => (
            <Table.Row key={task._id}>
              <Table.Cell>
                <Text weight="medium">{task.title}</Text>
              </Table.Cell>
              <Table.Cell>
                <StatusBadge status={task.status} />
              </Table.Cell>
              <Table.Cell>
                <Text size="1" color="gray">
                  {task.description ?? "—"}
                </Text>
              </Table.Cell>
              <Table.Cell>
                {task.dueAt ? <RelativeTime timestamp={task.dueAt} /> : "—"}
              </Table.Cell>
              <Table.Cell>
                <RelativeTime timestamp={task.createdAt} />
              </Table.Cell>
            </Table.Row>
          ))}
          {(tasks ?? []).length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={5}>
                <Text color="gray">No tasks.</Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>

      {/* Reminders Section */}
      <Heading size="3" mb="2" mt="4">
        Reminders
      </Heading>
      <Table.Root variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Content</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Scheduled For</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Created</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(reminders ?? []).map((reminder) => (
            <Table.Row key={reminder._id}>
              <Table.Cell>{reminder.content}</Table.Cell>
              <Table.Cell>
                <StatusBadge status={reminder.status} />
              </Table.Cell>
              <Table.Cell>
                <RelativeTime timestamp={reminder.scheduledFor} />
              </Table.Cell>
              <Table.Cell>
                <RelativeTime timestamp={reminder.createdAt} />
              </Table.Cell>
            </Table.Row>
          ))}
          {(reminders ?? []).length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={4}>
                <Text color="gray">No reminders.</Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>

      {/* Activities Section */}
      <Heading size="3" mb="2" mt="4">
        Activities
      </Heading>
      <Table.Root variant="surface">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Actor</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Content</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>Time</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {(activities ?? []).map((activity) => (
            <Table.Row key={activity._id}>
              <Table.Cell>
                <StatusBadge status={activity.activityType} />
              </Table.Cell>
              <Table.Cell>
                <StatusBadge status={activity.actor} />
              </Table.Cell>
              <Table.Cell>
                <Text size="2">{activity.content ?? "—"}</Text>
              </Table.Cell>
              <Table.Cell>
                <RelativeTime timestamp={activity.createdAt} />
              </Table.Cell>
            </Table.Row>
          ))}
          {(activities ?? []).length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={4}>
                <Text color="gray">No activities.</Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table.Root>
    </div>
  );
};
